// File: pages/_app.jsx
// Purpose: App wrapper for Pages Router. Loads global providers, headers/footers,
//          global modals, and global CSS. Also imports react-big-calendar CSS,
//          which must be imported at the app level in Next.js.

import 'react-big-calendar/lib/css/react-big-calendar.css';
import '/styles/globals.css';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { SessionProvider } from 'next-auth/react';
import { UserProvider } from '/context/UserContext';

import Header from '/components/Layout/Header';
import Footer from '/components/Layout/Footer';
import SMHeader from '/components/Shufflemaps/Header/Header';

import EmailConfirmationModal from '/components/Modals/EmailConfirmationModal';
import LandingModal from '/components/Modals/LandingModal';
import LoginModal from '/components/Modals/LoginModal';
import PerspectiveUploadModal from '/components/Modals/PerspectiveUploadModal';
import ModalPortal from '/components/Modals/ModalPortal';

function MyApp({ Component, pageProps }) {
  const router = useRouter();

  const [isEmailConfirmationModalOpen, setEmailConfirmationModalOpen] = useState(false);
  const [isLandingModalOpen, setLandingModalOpen] = useState(false);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isPerspectiveUploadModalOpen, setPerspectiveUploadModalOpen] = useState(false);
  const [modalContainer, setModalContainer] = useState(null);

  useEffect(() => {
    const container = document.getElementById('modal-root');
    setModalContainer(container);
  }, []);

  const isShuffleMapsRoute = router.pathname.startsWith('/ShuffleMaps');

  const isTrackDatRoute =
    router.pathname === '/TrackDat' ||
    router.pathname === '/ShuffleDB/MotionEditor' ||
    router.pathname === '/ShuffleDB/TrackDat';

  const page = (
    <Component
      {...pageProps}
      openEmailConfirmationModal={() => setEmailConfirmationModalOpen(true)}
      openLandingModal={() => setLandingModalOpen(true)}
      openLoginModal={() => setLoginModalOpen(true)}
      openPerspectiveUploadModal={() => setPerspectiveUploadModalOpen(true)}
    />
  );

  const defaultLayout = (pageContent) => (
    <>
      {isShuffleMapsRoute ? <SMHeader /> : <Header />}
      {pageContent}
      {!isShuffleMapsRoute && <Footer />}
    </>
  );

  const trackDatLayout = (pageContent) => pageContent;

  const getLayout =
    Component.getLayout ||
    (isTrackDatRoute ? trackDatLayout : defaultLayout);

  return (
    <SessionProvider
      session={pageProps.session}
      refetchInterval={10 * 60}
      refetchOnWindowFocus
      refetchWhenOffline={false}
    >
      <UserProvider>
        <>
          {modalContainer && (
            <ModalPortal>
              <EmailConfirmationModal
                isOpen={isEmailConfirmationModalOpen}
                onClose={() => setEmailConfirmationModalOpen(false)}
              />
              <LandingModal
                isOpen={isLandingModalOpen}
                onClose={() => setLandingModalOpen(false)}
              />
              <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setLoginModalOpen(false)}
              />
              <PerspectiveUploadModal
                isOpen={isPerspectiveUploadModalOpen}
                onClose={() => setPerspectiveUploadModalOpen(false)}
              />
            </ModalPortal>
          )}

          {getLayout(page)}
        </>
      </UserProvider>
    </SessionProvider>
  );
}

export default MyApp;
